import { KeyboardEvent, useId, useRef, useState } from 'react';

import {
  ChevronDownIcon,
  ChevronUpIcon,
  CircleCheckIcon,
  Loader2,
} from 'lucide-react';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'components/Collapsible';
import {
  CONFIDENCE_STYLES,
  SOURCE_CONFIDENCE_LABELS,
  sourceLevelFromPercent,
} from 'components/Kit/QuickQuoteCoach/utils/buildConfidenceDisplay';
import {
  LaneRecordKind,
  LaneRecords,
} from 'components/SuperDispatch/LaneRecords';
import { SuperDispatchPriceTrendChart } from 'components/SuperDispatch/SuperDispatchPriceTrendChart';
import { hasSuperDispatchPriceTrendChart } from 'components/SuperDispatch/buildSuperDispatchTrendChartData';
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from 'components/Tooltip';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { SuperDispatchInternalLaneContextStatus } from 'lib/api/getQuickQuote';
import { useQuickQuoteCurrency } from 'pages/QuoteView/Quoting/RequestQuickQuote/hooks/useQuickQuoteCurrency';
import { Maybe } from 'types/UtilityTypes';
import { Quoting, integrationNameMap } from 'types/enums/Integrations';
import { titleCase } from 'utils/formatStrings';
import { getDrumkitContainer } from 'utils/getDrumkitContainer';
import { cn } from 'utils/shadcn';

import type { QuoteCardType } from './QuoteCard';

const TOOLTIP_DELAY_MS = 10;

const TAB_ORDER = [LaneRecordKind.Postings, LaneRecordKind.Moves] as const;

const TAB_LABELS: Record<LaneRecordKind, string> = {
  [LaneRecordKind.Postings]: 'Posted',
  [LaneRecordKind.Moves]: 'Recent Moves',
};

const SUPER_DISPATCH_NAME = integrationNameMap[Quoting.SuperDispatch];

type QuoteCardSuperDispatchProps = {
  carrier: QuoteCardType;
  isSelected: boolean;
  onClick: () => void;
};

type PriceRangeValueProps = {
  range: string;
  perMileRange: Maybe<string>;
};

export function QuoteCardSuperDispatch({
  carrier,
  isSelected,
  onClick,
}: QuoteCardSuperDispatchProps) {
  const [detailsOpen, setDetailsOpen] = useState(true);
  // Start on Recent Moves: they come with the quote, while postings load later.
  const [activeTab, setActiveTab] = useState(LaneRecordKind.Moves);
  const tabsId = useId();
  // Refs, not getElementById: the sidebar can render inside a shadow root.
  const tabButtonRefs = useRef<
    Partial<Record<LaneRecordKind, Maybe<HTMLButtonElement>>>
  >({});
  const { formatAmount, formatRange, formatPerMileRate } =
    useQuickQuoteCurrency();

  const metadata = carrier.superDispatchMetadata;
  // Null when Super Dispatch sent no score or 0 (see parseQuoteCardsFromResponse).
  const confidence = carrier.confidence;
  const laneContextStatus = metadata?.internalLaneContextStatus;
  const isLaneContextPending =
    laneContextStatus === SuperDispatchInternalLaneContextStatus.Pending;
  const isLaneContextUnavailable =
    laneContextStatus === SuperDispatchInternalLaneContextStatus.Unavailable;
  const priceTrend = metadata?.priceTrend ?? [];
  const hasPriceTrend = hasSuperDispatchPriceTrendChart(priceTrend);
  const recordsByTab = {
    [LaneRecordKind.Postings]: metadata?.recentPostings,
    [LaneRecordKind.Moves]: metadata?.recentMoves,
  };

  const onTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }

    event.preventDefault();
    const nextTab = TAB_ORDER.find((tab) => tab !== activeTab);
    if (nextTab) {
      setActiveTab(nextTab);
      tabButtonRefs.current[nextTab]?.focus();
    }
  };

  return (
    <TooltipProvider>
      <div className='relative w-full'>
        <div
          className={cn(
            'w-full min-w-0 rounded-[4px] border-2 px-3 py-2 text-left transition-transform',
            isSelected
              ? 'border-brand-500 bg-brand-50'
              : 'border-neutral-400/70 bg-neutral-50 hover:scale-[1.01]'
          )}
        >
          <button
            type='button'
            aria-pressed={isSelected}
            aria-label={`Select ${SUPER_DISPATCH_NAME} Pricing Insights quote at ${formatAmount(carrier.cost)}`}
            onClick={onClick}
            className='w-full text-left'
          >
            <Flex direction='col' className='mb-2 w-full gap-0.5'>
              <Typography className='text-[14px] font-semibold text-neutral-800'>
                {carrier.title}
              </Typography>
              <Typography variant='body-xxs' className='text-neutral-500'>
                {`Recommended price · ${SUPER_DISPATCH_NAME}`}
              </Typography>
            </Flex>

            <Flex direction='col' className='w-full gap-0'>
              <Flex justify='between' align='baseline' className='w-full gap-3'>
                <Typography className='shrink-0 text-[13px] text-neutral-700'>
                  Transport Type
                </Typography>
                <Typography
                  variant='body-xs'
                  className='min-w-0 text-right tabular-nums'
                >
                  {titleCase(carrier.actualTransportType)}
                </Typography>
              </Flex>

              <Flex
                justify='between'
                align='baseline'
                className='mt-1 w-full gap-3'
              >
                <Typography className='shrink-0 text-[13px] text-neutral-700'>
                  Buy Rate
                </Typography>
                <Flex
                  align='baseline'
                  className='min-w-0 flex-wrap justify-end gap-x-2 tabular-nums'
                >
                  <Typography variant='body-xs'>
                    {formatAmount(carrier.cost)}
                  </Typography>
                  {carrier.costPerMile != null && (
                    <Typography variant='body-xs'>
                      {`(${formatPerMileRate(carrier.costPerMile, 2)}/mi)`}
                    </Typography>
                  )}
                </Flex>
              </Flex>

              {carrier.priceRange && (
                <Flex
                  justify='between'
                  align='baseline'
                  className='mt-1 w-full gap-3'
                >
                  <Typography className='shrink-0 text-[13px] text-neutral-700'>
                    Price Range
                  </Typography>
                  <PriceRangeValue
                    range={formatRange(
                      carrier.priceRange.lowEstimate,
                      carrier.priceRange.highEstimate
                    )}
                    perMileRange={
                      carrier.priceRangePerMile
                        ? `${formatPerMileRate(carrier.priceRangePerMile.lowEstimate, 2)} - ${formatPerMileRate(carrier.priceRangePerMile.highEstimate, 2)}/mi.`
                        : null
                    }
                  />
                </Flex>
              )}

              <Flex
                justify='between'
                align='baseline'
                className='mt-1 w-full gap-3'
              >
                <Typography className='shrink-0 text-[13px] text-neutral-700'>
                  Confidence
                </Typography>
                <Typography
                  variant='body-xs'
                  weight='medium'
                  className={confidenceClassName(confidence)}
                >
                  {confidenceText(confidence)}
                </Typography>
              </Flex>
            </Flex>
          </button>

          <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
            <CollapsibleTrigger
              type='button'
              className='mt-1.5 w-full border-t border-neutral-200 pt-1.5 text-brand-500'
            >
              <Flex justify='center' align='center' gap='xs'>
                <Typography
                  as='span'
                  variant='body-xxs'
                  weight='medium'
                  className='text-inherit'
                >
                  {detailsOpen ? 'Hide lane insights' : 'Show lane insights'}
                </Typography>
                {detailsOpen ? (
                  <ChevronUpIcon className='h-3 w-3' aria-hidden />
                ) : (
                  <ChevronDownIcon className='h-3 w-3' aria-hidden />
                )}
              </Flex>
            </CollapsibleTrigger>
            <CollapsibleContent className='min-w-0 space-y-2 pt-1.5'>
              {hasPriceTrend && (
                <SuperDispatchPriceTrendChart
                  priceTrend={priceTrend}
                  formatAmount={formatAmount}
                />
              )}
              {!hasPriceTrend && isLaneContextPending && (
                <Flex
                  justify='center'
                  align='center'
                  gap='xs'
                  className='min-h-12 rounded border border-neutral-200 bg-neutral-50 py-2 text-neutral-500'
                >
                  <Loader2
                    className='h-3 w-3 animate-spin'
                    aria-label='Loading price trend'
                  />
                  <Typography
                    variant='body-xxs'
                    weight='semibold'
                    className='text-neutral-500'
                  >
                    Loading price trend...
                  </Typography>
                </Flex>
              )}

              <Flex
                role='tablist'
                gap='md'
                className='border-b border-neutral-200'
              >
                {TAB_ORDER.map((tab) => {
                  const isActive = activeTab === tab;
                  const isPendingTab =
                    tab === LaneRecordKind.Postings && isLaneContextPending;
                  const count = recordsByTab[tab]?.length;

                  return (
                    <button
                      key={tab}
                      ref={(element) => {
                        tabButtonRefs.current[tab] = element;
                      }}
                      id={`${tabsId}-${tab}-tab`}
                      type='button'
                      role='tab'
                      aria-selected={isActive}
                      aria-controls={`${tabsId}-panel`}
                      tabIndex={isActive ? 0 : -1}
                      onClick={() => setActiveTab(tab)}
                      onKeyDown={onTabKeyDown}
                      className={cn(
                        '-mb-px border-b-2 pb-1',
                        isActive
                          ? 'border-brand-500 text-brand-500'
                          : 'border-transparent text-neutral-500'
                      )}
                    >
                      <Flex align='center' gap='xs'>
                        <Typography
                          as='span'
                          variant='body-xxs'
                          weight='semibold'
                          className='text-inherit'
                        >
                          {TAB_LABELS[tab]}
                          {!isPendingTab && count != null && ` (${count})`}
                        </Typography>
                        {isPendingTab && (
                          <Loader2
                            className='h-3 w-3 animate-spin'
                            aria-label='Loading postings'
                          />
                        )}
                      </Flex>
                    </button>
                  );
                })}
              </Flex>

              <div
                id={`${tabsId}-panel`}
                role='tabpanel'
                aria-labelledby={`${tabsId}-${activeTab}-tab`}
                className='mt-1 min-w-0'
              >
                <LaneRecords
                  key={activeTab}
                  rows={recordsByTab[activeTab]}
                  kind={activeTab}
                  isPending={
                    activeTab === LaneRecordKind.Postings &&
                    isLaneContextPending
                  }
                  formatAmount={formatAmount}
                  formatPerMileRate={formatPerMileRate}
                />
              </div>

              {isLaneContextUnavailable && (
                <div
                  className='mt-2 rounded border border-warning-200 bg-warning-50 px-2 py-1.5'
                  role='status'
                >
                  <Typography variant='body-xxs' className='text-neutral-800'>
                    Postings and price trend are unavailable right now.
                  </Typography>
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>
        </div>

        {isSelected && (
          <CircleCheckIcon
            data-testid='quote-card-selected-check'
            className='absolute -right-2.5 -top-3 z-10 h-6 w-6 fill-neutral-50 stroke-success-500!'
            aria-hidden
          />
        )}
      </div>
    </TooltipProvider>
  );
}

function PriceRangeValue({ range, perMileRange }: PriceRangeValueProps) {
  const rangeText = (
    <Typography variant='body-xs' className='tabular-nums'>
      {range}
    </Typography>
  );

  if (!perMileRange) {
    return rangeText;
  }

  return (
    <Tooltip delayDuration={TOOLTIP_DELAY_MS}>
      <TooltipTrigger asChild>
        <span className='border-b border-dashed border-neutral-900'>
          {rangeText}
        </span>
      </TooltipTrigger>
      <TooltipPortal container={getDrumkitContainer()}>
        <TooltipContent className='mr-2 max-w-60 whitespace-pre-wrap'>
          <div>
            <Typography variant='body-xs'>Price range per mile</Typography>
            <Typography variant='body-xs'>{perMileRange}</Typography>
          </div>
        </TooltipContent>
      </TooltipPortal>
    </Tooltip>
  );
}

function confidenceText(confidence: Maybe<number>): string {
  if (confidence == null) {
    return 'Unavailable';
  }

  const label = SOURCE_CONFIDENCE_LABELS[sourceLevelFromPercent(confidence)];
  return `${Math.round(confidence)}% (${label})`;
}

function confidenceClassName(confidence: Maybe<number>): string {
  if (confidence == null) {
    return 'text-neutral-500';
  }

  return CONFIDENCE_STYLES[sourceLevelFromPercent(confidence)].className;
}
